import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { ExcelTable } from "@/components/ExcelTable";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Inbox, X, Loader2, FileSpreadsheet, FileText, ChevronDown, Trash2, Pencil, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import ReceiptButton from "@/components/ReceiptButton";
import RequestEditDialog from "@/components/RequestEditDialog";

const fmtEur = (n) =>
  "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Rome" }) : "-";
const fmtDateTime = (s) =>
  s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }) : "-";
const STATUS_LABEL = { in_attesa: "In attesa", approvata: "Approvata", rifiutata: "Rifiutata" };

export default function StoricoRichieste() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("tutte");
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [exporting, setExporting] = useState("");
  const [detail, setDetail] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [editReq, setEditReq] = useState(null);

  const load = () => {
    setLoading(true);
    const params = filter === "tutte" ? {} : { status: filter };
    api.get("/requests", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  const refresh = () => {
    const params = filter === "tutte" ? {} : { status: filter };
    return api.get("/requests", { params }).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(load, [filter]);
  usePolling(refresh, 15000);

  const employeeOptions = useMemo(() => {
    const map = new Map();
    items.forEach((r) => { if (r.employee_id) map.set(r.employee_id, r.employee_name); });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "it"));
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((r) => {
      if (selectedEmployees.length && !selectedEmployees.includes(r.employee_id)) return false;
      const d = r.created_at ? r.created_at.slice(0, 10) : "";
      if (dateFrom && d && d < dateFrom) return false;
      if (dateTo && d && d > dateTo) return false;
      return true;
    });
  }, [items, selectedEmployees, dateFrom, dateTo]);

  const rows = useMemo(() => filtered.map((r) => ({
    ...r,
    created_str: fmtDate(r.created_at),
    usage_str: `${r.usage_value} ${r.usage_type === "km" ? "km" : "ore"}`,
    status_label: STATUS_LABEL[r.status] || r.status,
    liters: r.liters ?? null,
  })), [filtered]);

  const toggleEmployee = (id) => setSelectedEmployees((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const hasFilters = dateFrom || dateTo || selectedEmployees.length > 0;
  const resetFilters = () => { setDateFrom(""); setDateTo(""); setSelectedEmployees([]); };

  const doDelete = async () => {
    try {
      await api.delete(`/requests/${toDelete.id}`);
      toast.success("Richiesta eliminata");
      setToDelete(null);
      load();
    } catch (e) { toast.error("Errore durante l'eliminazione"); }
  };

  const exportRequests = async (format) => {
    setExporting(format);
    try {
      const params = { format };
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      if (filter !== "tutte") params.status = filter;
      if (selectedEmployees.length) params.employee_id = selectedEmployees.join(",");
      const res = await api.get("/requests/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `storico_richieste.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Report ${format.toUpperCase()} scaricato`);
    } catch (e) { toast.error("Errore durante l'esportazione"); }
    finally { setExporting(""); }
  };

  const columns = [
    { key: "created_str", header: "Data", sortValue: (r) => new Date(r.created_at).getTime(), className: "whitespace-nowrap text-slate-500", cell: (r) => r.created_str },
    { key: "employee_name", header: "Dipendente", className: "font-medium text-slate-900", cell: (r) => (
        <span>{r.employee_name}{r.source === "flux" ? <span className="ml-2 inline-block rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700" data-testid={`flux-badge-${r.id}`}>Flux</span> : null}{r.created_by_name ? <span className="ml-2 inline-block rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-[#002FA7]" data-testid={`on-behalf-badge-${r.id}`}>Per conto di · {r.created_by_name}</span> : null}{r.edit_requested ? <span className="ml-2 inline-block rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`edit-req-badge-${r.id}`}>Modifica richiesta</span> : null}</span>
      ) },
    { key: "fuel_card", header: "Carta", className: "text-slate-600", cell: (r) => r.fuel_card || "-" },
    { key: "vehicle_name", header: "Mezzo", className: "text-slate-600", cell: (r) => r.vehicle_name },
    { key: "vehicle_plate", header: "Targa", className: "whitespace-nowrap text-slate-600", cell: (r) => r.vehicle_plate || "-" },
    { key: "usage_str", header: "Utilizzo", className: "text-slate-600", cell: (r) => r.usage_str },
    { key: "fuel_type", header: "Carburante", className: "text-slate-600", cell: (r) => r.fuel_type },
    { key: "amount", header: "Importo aut.", type: "number", align: "right", className: "text-right font-heading font-bold text-slate-900", cell: (r) => fmtEur(r.amount) },
    { key: "actual_amount", header: "Importo eff.", type: "number", align: "right", sortValue: (r) => (r.actual_amount == null ? -1 : r.actual_amount), className: "text-right", cell: (r) => (r.actual_amount != null ? <span className="font-heading font-semibold text-emerald-700">{fmtEur(r.actual_amount)}</span> : <span className="text-slate-300">—</span>) },
    { key: "liters", header: "Litri", type: "number", align: "right", sortValue: (r) => (r.liters == null ? -1 : r.liters), className: "text-right", cell: (r) => (r.liters != null ? <span className="text-slate-700">{fmtNum(r.liters)} l</span> : <span className="text-slate-300">—</span>) },
    { key: "status_label", header: "Stato", cell: (r) => <StatusBadge status={r.status} /> },
    { key: "reviewed_by", header: "Gestita da", className: "text-xs text-slate-500", cell: (r) => r.reviewed_by || "-" },
    { key: "actions", header: "Azioni", align: "right", cell: (r) => (
        <div className="flex items-center justify-end gap-2">
          {r.receipt_path && <ReceiptButton path={r.receipt_path} />}
          <Button size="sm" variant="outline" onClick={() => setDetail(r)} data-testid={`storico-info-btn-${r.id}`} className="rounded-md border-slate-200"><Info className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setEditReq(r)} data-testid={`storico-edit-btn-${r.id}`} className="rounded-md border-slate-200"><Pencil className="h-4 w-4" /></Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setToDelete(r)} data-testid={`storico-delete-btn-${r.id}`} className="rounded-md border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
          )}
        </div>
      ) },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Storico richieste</h1>
          <p className="mt-1 text-sm text-slate-500">Elenco completo delle richieste. Ordina e filtra ogni colonna dall'intestazione, come in Excel.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3 lg:justify-end">
          <div>
            <Label htmlFor="date-from" className="text-xs text-slate-500">Dal</Label>
            <Input id="date-from" type="date" data-testid="storico-date-from" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} className="mt-1.5 w-40" />
          </div>
          <div>
            <Label htmlFor="date-to" className="text-xs text-slate-500">Al</Label>
            <Input id="date-to" type="date" data-testid="storico-date-to" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} className="mt-1.5 w-40" />
          </div>
          <div>
            <Label className="block text-xs text-slate-500">Dipendente</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="storico-employee-select" className="mt-1.5 h-10 w-56 justify-between rounded-md font-normal">
                  <span className="truncate">
                    {selectedEmployees.length === 0 ? "Tutti i dipendenti" : selectedEmployees.length === 1 ? (employeeOptions.find((o) => o.id === selectedEmployees[0])?.name || "1 selezionato") : `${selectedEmployees.length} selezionati`}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
                <DropdownMenuLabel className="flex items-center justify-between">
                  Dipendenti
                  {selectedEmployees.length > 0 && (
                    <button onClick={() => setSelectedEmployees([])} data-testid="storico-employee-clear" className="text-xs font-normal text-[#002FA7] hover:underline">Deseleziona</button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {employeeOptions.length === 0 ? (
                  <p className="px-2 py-2 text-xs text-slate-400">Nessun dipendente</p>
                ) : employeeOptions.map((o) => (
                  <DropdownMenuCheckboxItem key={o.id} checked={selectedEmployees.includes(o.id)} onCheckedChange={() => toggleEmployee(o.id)} onSelect={(e) => e.preventDefault()} data-testid={`storico-employee-option-${o.id}`}>{o.name}</DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {hasFilters && (
            <Button variant="outline" onClick={resetFilters} data-testid="storico-reset-filters" className="h-10 rounded-md"><X className="mr-2 h-4 w-4" /> Azzera</Button>
          )}
          <Button variant="outline" onClick={() => exportRequests("xlsx")} disabled={!!exporting} data-testid="storico-export-xlsx" className="h-10 rounded-md">
            {exporting === "xlsx" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Excel
          </Button>
          <Button onClick={() => exportRequests("pdf")} disabled={!!exporting} data-testid="storico-export-pdf" className="h-10 rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
            {exporting === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} PDF
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="storico-filter-tabs">
            <TabsTrigger value="tutte" data-testid="storico-filter-tutte">Tutte</TabsTrigger>
            <TabsTrigger value="in_attesa" data-testid="storico-filter-in_attesa">In attesa</TabsTrigger>
            <TabsTrigger value="approvata" data-testid="storico-filter-approvata">Approvate</TabsTrigger>
            <TabsTrigger value="rifiutata" data-testid="storico-filter-rifiutata">Rifiutate</TabsTrigger>
          </TabsList>
        </Tabs>
        <span className="text-sm text-slate-400" data-testid="storico-result-count">{rows.length} {rows.length === 1 ? "richiesta" : "richieste"}</span>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm">
        {loading ? (
          <p className="p-6 text-slate-400">Caricamento...</p>
        ) : (
          <div className="p-4">
            <ExcelTable data={rows} testid="storico-requests-table" initialSort={{ key: "created_str", dir: "desc" }} emptyIcon={Inbox} emptyText="Nessuna richiesta" columns={columns} />
          </div>
        )}
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="storico-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Storico richiesta</DialogTitle>
            <DialogDescription>{detail?.employee_name} · {detail?.vehicle_name} · {fmtEur(detail?.amount)}</DialogDescription>
          </DialogHeader>
          <ol className="relative ml-1 space-y-5 border-l border-slate-200 pl-6">
            <li className="relative">
              <span className="absolute -left-[30px] mt-1 h-3.5 w-3.5 rounded-full bg-[#002FA7]" />
              <p className="font-medium text-slate-900">Richiesta creata</p>
              <p className="text-sm text-slate-500" data-testid="detail-created">{fmtDateTime(detail?.created_at)}</p>
            </li>
            <li className="relative">
              <span className={`absolute -left-[30px] mt-1 h-3.5 w-3.5 rounded-full ${detail?.status === "approvata" ? "bg-emerald-500" : detail?.status === "rifiutata" ? "bg-rose-500" : "bg-slate-300"}`} />
              <p className="font-medium text-slate-900">{detail?.status === "rifiutata" ? "Richiesta rifiutata" : detail?.status === "approvata" ? "Richiesta approvata" : "In attesa di approvazione"}</p>
              {detail?.reviewed_at ? (
                <p className="text-sm text-slate-500" data-testid="detail-reviewed">{fmtDateTime(detail.reviewed_at)}{detail.reviewed_by ? ` · da ${detail.reviewed_by}` : ""}</p>
              ) : (<p className="text-sm text-slate-400">Non ancora gestita</p>)}
            </li>
            <li className="relative">
              <span className={`absolute -left-[30px] mt-1 h-3.5 w-3.5 rounded-full ${(detail?.receipt_uploaded_at || detail?.receipt_path) ? "bg-emerald-500" : "bg-slate-300"}`} />
              <p className="font-medium text-slate-900">Scontrino caricato</p>
              {detail?.receipt_uploaded_at || detail?.receipt_path ? (
                <p className="text-sm text-slate-500" data-testid="detail-receipt">
                  {detail?.receipt_uploaded_at ? fmtDateTime(detail.receipt_uploaded_at) : "Caricato"}
                  {detail?.actual_amount != null ? ` · Importo effettivo ${fmtEur(detail.actual_amount)}` : ""}
                  {detail?.liters != null ? ` · ${fmtNum(detail.liters)} l` : ""}
                </p>
              ) : (<p className="text-sm text-slate-400">Non caricato</p>)}
            </li>
          </ol>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent data-testid="storico-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Elimina richiesta</DialogTitle>
            <DialogDescription>Eliminare definitivamente la richiesta di <b>{toDelete?.employee_name}</b> da {fmtEur(toDelete?.amount)}? L'operazione non è reversibile.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>Annulla</Button>
            <Button onClick={doDelete} data-testid="storico-confirm-delete-btn" className="bg-rose-600 text-white hover:bg-rose-700">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RequestEditDialog request={editReq} open={!!editReq} onClose={() => setEditReq(null)} onSaved={load} />
    </div>
  );
}
